import React from 'react';
import { Heart, Users, Award, Shield, Scissors, Palette } from 'lucide-react';
import Header from './components/Header13';
import Footer from './components/Footer13';

const About13 = () => {
    const values = [
        { icon: Heart, title: 'Handcrafted with Love', desc: 'Every piece is made by hand using traditional techniques passed down through generations.' },
        { icon: Users, title: 'Artisan Partnerships', desc: 'We work directly with over 500 artisan families across India, ensuring fair wages and sustainable livelihoods.' },
        { icon: Shield, title: 'Authentic Craft', desc: 'Each product comes with a certificate of authenticity and the artisan\'s signature mark.' },
        { icon: Palette, title: 'Natural Materials', desc: 'We prioritize natural dyes, organic fabrics, and eco-friendly processes in all our crafts.' },
    ];

    const milestones = [
        { year: '2018', event: 'Founded in Jaipur with 5 artisan families' },
        { year: '2019', event: 'Expanded to Varanasi handloom weavers' },
        { year: '2021', event: 'Reached 200 artisan partnerships' },
        { year: '2023', event: '500+ artisan families across 12 states' },
        { year: '2026', event: 'Serving craft lovers in 15+ countries' },
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Mulish:wght@300;400;600&display=swap');`}</style>
            <div style={{ fontFamily: "'Mulish', sans-serif" }} className="min-h-screen bg-[#FBF8F4]">
                <Header />
                <main style={{ paddingTop: '72px' }}>
                    {/* Hero */}
                    <div style={{ position: 'relative', height: '50vh', minHeight: '300px' }}>
                        <img src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Artisan at work" className="w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(28, 25, 23, 0.8) 0%, rgba(28, 25, 23, 0.3) 100%)' }} />
                        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-16" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                            <p style={{ fontSize: '12px', letterSpacing: '4px', color: '#C2410C', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>Our Story</p>
                            <h1 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '36px', fontWeight: 700, color: '#FBF8F4', lineHeight: 1.2 }} className="lg:text-5xl">
                                Preserving India's<br />Craft Heritage
                            </h1>
                        </div>
                    </div>

                    {/* Story */}
                    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '60px 20px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <div style={{ width: '48px', height: '3px', backgroundColor: '#C2410C', margin: '0 auto 24px' }} />
                            <h2 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#1C1917', marginBottom: '20px' }} className="lg:text-4xl">
                                Where Every Thread Tells a Story
                            </h2>
                            <p style={{ fontSize: '16px', color: '#78716C', lineHeight: 1.8, marginBottom: '16px' }}>
                                KARIGAR was born from a deep reverence for India's textile traditions. In a world of fast fashion, we chose a different path -- one that honors the hands that weave, print, and paint the fabrics that have defined Indian artistry for millennia.
                            </p>
                            <p style={{ fontSize: '16px', color: '#78716C', lineHeight: 1.8 }}>
                                We travel to remote villages across Rajasthan, Madhya Pradesh, Andhra Pradesh, Uttar Pradesh, and beyond, partnering directly with master artisans. Each piece in our collection represents months of skill, patience, and cultural heritage.
                            </p>
                        </div>
                    </div>

                    {/* Values */}
                    <div style={{ backgroundColor: '#F7F3ED', padding: '60px 20px' }}>
                        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                <p style={{ fontSize: '12px', letterSpacing: '4px', color: '#C2410C', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>Our Values</p>
                                <h2 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#1C1917' }} className="lg:text-4xl">What We Stand For</h2>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                {values.map((v, i) => (
                                    <div key={i} style={{ backgroundColor: '#FBF8F4', borderRadius: '8px', padding: '28px', border: '1px solid #E7E0D6', textAlign: 'center' }}>
                                        <v.icon size={28} style={{ color: '#C2410C', margin: '0 auto 16px' }} />
                                        <h3 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '17px', fontWeight: 700, color: '#1C1917', marginBottom: '8px' }}>{v.title}</h3>
                                        <p style={{ fontSize: '13px', color: '#78716C', lineHeight: 1.6 }}>{v.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '60px 20px 80px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                            <p style={{ fontSize: '12px', letterSpacing: '4px', color: '#C2410C', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>Our Journey</p>
                            <h2 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#1C1917' }} className="lg:text-4xl">Milestones</h2>
                        </div>
                        <div>
                            {milestones.map((m, i) => (
                                <div key={i} style={{ display: 'flex', gap: '20px', marginBottom: '28px', alignItems: 'flex-start' }}>
                                    <div style={{ width: '60px', flexShrink: 0, textAlign: 'right' }}>
                                        <span style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '18px', fontWeight: 700, color: '#C2410C' }}>{m.year}</span>
                                    </div>
                                    <div style={{ width: '3px', backgroundColor: '#E7E0D6', position: 'relative', flexShrink: 0 }}>
                                        <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#C2410C', position: 'absolute', top: '4px', left: '-4.5px' }} />
                                    </div>
                                    <div style={{ flex: 1, paddingTop: '2px' }}>
                                        <p style={{ fontSize: '15px', color: '#1C1917', lineHeight: 1.6 }}>{m.event}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA */}
                    <div style={{ backgroundColor: '#C2410C', padding: '60px 20px', textAlign: 'center' }}>
                        <h2 style={{ fontFamily: "'Merriweather', Georgia, serif", fontSize: '28px', fontWeight: 700, color: '#FBF8F4', marginBottom: '16px' }} className="lg:text-4xl">
                            Support Artisan Livelihoods
                        </h2>
                        <p style={{ fontSize: '16px', color: 'rgba(251, 248, 244, 0.85)', marginBottom: '32px', maxWidth: '500px', margin: '0 auto 32px' }}>
                            Every purchase directly supports an artisan family and keeps an ancient craft alive.
                        </p>
                        <button onClick={() => window.location.href = '/template13/shop'}
                            style={{ padding: '16px 40px', backgroundColor: '#FBF8F4', color: '#C2410C', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.3s' }}>
                            Shop Now
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default About13;
